import React, { useState, useRef, useEffect } from 'react';
import type { InteractionPart } from '@lib/models/InteractionPart';
import MarkdownExtended from "../../Library/MarkdowmExtended";
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Timer } from "@mui/icons-material";
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { EventType } from '@lib/models/enums';
import { splitBigLinesWithSpaces } from '@lib/utils/stringUtils';
import LoadingDots from '../../Library/LoadingDots';
import Stopwatch from '../../Stopwatch';

interface MessageNonTextDisplayProps {
    title: string;
    icon: React.ReactNode;
    elapsed?: number;
    content?: string;
    part: InteractionPart;
    sessionId: string;
    onLinkClicked: (link: string, content: string | null) => void;
    onDrawCodeBlock: (code: string, className: string) => React.ReactNode;
    isLastInlineInGroup?: boolean;
    preview?: boolean;
}

const MessageNonTextDisplay: React.FC<MessageNonTextDisplayProps> = ({
    title,
    icon,
    elapsed,
    sessionId,
    content,
    part,
    onLinkClicked,
    preview,
    onDrawCodeBlock,
    isLastInlineInGroup = false,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const thinkingContentRef = useRef<HTMLDivElement>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const open = Boolean(anchorEl);

    const handleViewClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const preStyle: React.CSSProperties = {
        padding: '0px',
        borderRadius: '0px',
        margin: '0px',
    };

    const functionStatus = part.functionDetails?.status;
    const isFunctionCall =
        part.eventType === EventType.PartialFunctionCall ||
        part.eventType === EventType.FunctionCall ||
        part.eventType === EventType.FunctionResponse;
    const functionStartTime = part.functionDetails?.start?.getTime();
    const functionEndTime = part.functionDetails?.end?.getTime();
    const showFull = isLastInlineInGroup || (isFunctionCall && functionStatus !== 'Done');

    const parallaxAnimationClass = showFull ? 'parallax-container' : '';

    useEffect(() => {
        if (thinkingContentRef.current && showFull) {
            thinkingContentRef.current.scrollTo({
                top: thinkingContentRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [content, showFull]);

    const boxColor = theme.palette.text.secondary;
    // Theme-adaptive surfaces. These were hardcoded translucent-white (built for a
    // dark chat surface) and vanished on a light theme — the box rendered with no
    // visible fill or border. `action.hover` / `divider` resolve correctly in both
    // modes and stay ≈ the old values on dark, so the dark-theme look is preserved.
    const boxBackground = theme.palette.action.hover;
    const boxBorder = theme.palette.divider;
    const shimmer = theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.06)';

    return (
        <>
            <Box
                ref={containerRef}
                className={parallaxAnimationClass}
                sx={{
                    border: `1px solid ${boxBorder}`,
                    borderRadius: '4px',
                    maxWidth: showFull ? '100%' : '100%',
                    maxHeight: showFull ? '160px' : '20px',
                    minHeight: showFull ? '80px' : '20px',
                    margin: '2px 1px',
                    color: boxColor,
                    backgroundColor: boxBackground,
                    fontSize: '10px',
                    fontWeight: '500',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    boxSizing: 'border-box'
                }}
            >
                {isLastInlineInGroup && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: `linear-gradient(90deg, transparent, ${shimmer}, transparent)`,
                            animation: 'parallax-light 2s ease-in-out infinite',
                            borderRadius: '8px',
                            pointerEvents: 'none',
                            zIndex: 1
                        }}
                    />
                )}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: boxColor,
                    borderBottom: `1px solid ${boxBorder}`,
                    minHeight: '20px',
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    gap: '8px',
                    paddingLeft: 1,
                    paddingRight: 1,
                    zIndex: 2
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                        {icon}
                        <span style={{
                            marginLeft: '4px',
                            fontSize: '9px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {title}
                        </span>
                        {elapsed && elapsed > 0 ? (
                            <>
                                <Timer sx={{ fontSize: '12px', mt: 0.2, ml: 2 }} />
                                <span style={{
                                    marginLeft: '4px',
                                    fontSize: '9px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {(elapsed / 1000).toFixed(2)} s
                                </span>
                            </>
                        ) : null}
                    </div>

                    {!isMobile && (
                        <IconButton
                            size="small"
                            onClick={handleViewClick}
                            sx={{
                                color: boxColor,
                                padding: '1px',
                                minWidth: '16px',
                                width: '16px',
                                height: '16px',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.selected,
                                },
                            }}
                        >
                            <VisibilityIcon style={{ fontSize: 10 }} />
                        </IconButton>
                    )}
                </Box>
                {showFull && (
                    <Box
                        ref={thinkingContentRef}
                        sx={{
                            width: '100%',
                            backgroundColor: boxBackground,
                            fontSize: '10px',
                            margin: '0px !important',
                            fontWeight: '500',
                            overflow: "auto",
                            flex: 1,
                            '&::-webkit-scrollbar': {
                                width: '5px',
                                background: theme.palette.background.paper,
                            },
                            '&::-webkit-scrollbar-track': {
                                background: theme.palette.background.paper,
                                width: isMobile ? '1px' : '1px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: theme.palette.background.paper,
                                border: '1px solid',
                                width: '5px',
                                borderColor: theme.palette.grey[800],
                                borderRadius: '4px',
                                '&:hover': {
                                    background: theme.palette.background.default
                                },
                            }
                        }}
                    >
                        <MarkdownExtended
                            sessionId={sessionId}
                            preStyle={preStyle}
                            partial={preview}
                            text={splitBigLinesWithSpaces(content ?? "")}
                            onLinkClicked={onLinkClicked}
                            onDrawCodeBlock={onDrawCodeBlock}
                        />
                    </Box>
                )}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingBottom: '5x',
                    paddingTop: '5x',
                    paddingLeft: '10px',
                    color: boxColor,
                    backgroundColor: 'background.paper',
                    borderBottom: `1px solid ${boxBorder}`,
                    minHeight: '25px',
                    position: 'relative',
                    fontSize: '10px',
                    overflow: 'hidden',
                    borderTop: `1px solid ${boxBorder}`,
                    zIndex: 2
                }}>
                    {functionStatus === 'Composing' && (
                        <Stack direction='row' gap={1} sx={{ mt: 0.5 }}>
                            <Typography sx={{ fontSize: '10px' }}>Composing</Typography>
                            <LoadingDots size={3} color={theme.palette.primary.main} />
                        </Stack>
                    )}
                    {functionStatus === 'Executing' && (
                        <Stack direction='row' gap={1} sx={{ mt: 0.5, fontSize: '10px' }}>
                            <Typography sx={{ fontSize: '10px' }}>Running:</Typography>
                            <Timer sx={{ fontSize: '12px', mt: 0.2 }} />
                            <Stopwatch startingPoint={part.functionDetails?.start ?? new Date()} on={true} />
                        </Stack>
                    )}
                    {functionStatus === 'Done' && (
                        <Stack direction='row' gap={1} sx={{ mt: 0.5, fontSize: '10px' }}>
                            <Typography sx={{ fontSize: '10px' }}>Done!</Typography>
                            <Timer sx={{ fontSize: '12px', mt: 0.2 }} />
                            <Typography sx={{ fontSize: '10px' }}>
                                {functionEndTime && functionStartTime
                                    ? `${((functionEndTime - functionStartTime) / 1000).toFixed(2)}s`
                                    : `N\\D`}
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Box>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    style: {
                        backgroundColor: boxBackground,
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${boxBorder}`,
                        borderRadius: '8px',
                        maxWidth: '500px',
                        maxHeight: '400px',
                        color: boxColor,
                    }
                }}
            >
                <Box id="preview-container"
                    sx={{
                        '&::-webkit-scrollbar': {
                            width: isMobile ? 'none' : '8px',
                            height: isMobile ? 'none' : '8px',
                            background: theme.palette.background.paper,
                        },
                        '&::-webkit-scrollbar-track': {
                            background: theme.palette.background.paper,
                            width: isMobile ? 'none' : '5px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: theme.palette.background.default,
                            border: '1px solid',
                            borderColor: theme.palette.grey[800],
                            borderRadius: '4px',
                            '&:hover': {
                                background: theme.palette.grey[500],
                            },
                        },
                        padding: '12px',
                        overflow: 'scroll',
                        fontSize: '11px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        paddingBottom: '8px',
                        borderBottom: `1px solid ${boxBorder}`
                    }}>
                        {icon}
                        <span style={{ marginLeft: '8px', fontWeight: '500' }}>{title}</span>
                    </div>
                    <MarkdownExtended
                        sessionId={sessionId}
                        preStyle={preStyle}
                        text={content ?? ""}
                        onLinkClicked={onLinkClicked}
                        onDrawCodeBlock={onDrawCodeBlock}
                    />
                </Box>
            </Popover>

            {isLastInlineInGroup && !showFull && (
                <style>
                    {`
                        @keyframes parallax-light {
                            0% { left: -100%; }
                            100% { left: 100%; }
                        }
                    `}
                </style>
            )}
        </>
    );
};

export default MessageNonTextDisplay;
